import { useCallback } from 'react';

/**
 * Phase 9.15c: Navigation/Pagination Fix
 *
 * Hook to manage referrer page for list→detail→list navigation.
 * Stores the current page before navigating to detail pages,
 * so "back to list" links can restore the user's position.
 *
 * Uses sessionStorage for persistence across page refreshes.
 */

type ListType = 'invoices' | 'projects' | 'organizations' | 'permits';

/**
 * Hook to store and retrieve the referrer page for list navigation.
 *
 * @param listType - The type of list (invoices, projects, etc.)
 *
 * @example
 * // In list page - store page before navigation
 * const { storeCurrentPage } = useReferrerPage('invoices');
 * const handleRowClick = (id: string) => {
 *   storeCurrentPage(page);
 *   navigate(`/invoices/${id}`);
 * };
 *
 * @example
 * // In detail page - get back URL
 * const { getBackToListUrl } = useReferrerPage('invoices');
 * <Button onClick={() => navigate(getBackToListUrl())}>Back to Invoices</Button>
 */
export function useReferrerPage(listType: ListType) {
  const storageKey = `referrer_page_${listType}`;
  const basePath = `/${listType}`;

  /**
   * Store the current page before navigating away from the list.
   * Call this in onClick handlers before navigate().
   */
  const storeCurrentPage = useCallback(
    (page: number) => {
      if (page <= 1) {
        // Page 1 is default, no need to store (keeps URLs clean)
        sessionStorage.removeItem(storageKey);
      } else {
        sessionStorage.setItem(storageKey, String(page));
      }
    },
    [storageKey]
  );

  /**
   * Get the stored page number (for programmatic use).
   * Returns 1 if no page stored or invalid.
   */
  const getStoredPage = useCallback(() => {
    const stored = sessionStorage.getItem(storageKey);
    if (!stored) return 1;
    const parsed = parseInt(stored, 10);
    return isNaN(parsed) || parsed < 1 ? 1 : parsed;
  }, [storageKey]);

  /**
   * Get the URL to navigate back to the list with the stored page.
   * Use this for "Back to [List]" links.
   */
  const getBackToListUrl = useCallback(() => {
    const page = getStoredPage();
    if (page <= 1) {
      return basePath;
    }
    return `${basePath}?page=${page}`;
  }, [basePath, getStoredPage]);

  /**
   * Clear the stored page (call after successful return to list).
   * Optional - sessionStorage clears on tab close anyway.
   */
  const clearStoredPage = useCallback(() => {
    sessionStorage.removeItem(storageKey);
  }, [storageKey]);

  return {
    storeCurrentPage,
    getStoredPage,
    getBackToListUrl,
    clearStoredPage,
  };
}
